import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PlayerAvatar from "../components/PlayerAvatar";
import {
  Users, CheckCircle, XCircle, Percent, Trophy, Cake, SoccerBall,
  ChartLine, ClipboardText, FilePdf, UserPlus, Star, Plus, Megaphone,
  Medal, Shield, HandHeart, TrendUp, SoccerBall as Ball
} from "@phosphor-icons/react";

const StatCard = ({ label, value, icon: Icon, accent = "#F5F5F5", testid, sub, to }) => {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={22} weight="bold" color={accent} />
      </div>
      <div className="font-display font-black text-4xl mt-2 leading-none" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-2">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="block bg-[#121212] border border-neutral-800 rounded-lg p-5 card-lift hover:border-neutral-700">
        {inner}
      </Link>
    );
  }
  return (
    <div className="bg-[#121212] border border-neutral-800 rounded-lg p-5 card-lift" data-testid={testid}>
      {inner}
    </div>
  );
};

const QuickAction = ({ to, label, icon: Icon, color }) => (
  <Link
    to={to}
    data-testid={`quick-${label.toLowerCase().replace(/\s/g, "-")}`}
    className="group bg-[#121212] border border-neutral-800 rounded-lg p-5 h-full flex flex-col justify-between hover:border-[#D90429] card-lift"
  >
    <Icon size={32} weight="bold" color={color} />
    <div className="font-display font-bold uppercase tracking-tight text-lg mt-6 leading-none">{label}</div>
    <div className="overline mt-2">Tap to open</div>
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [awards, setAwards] = useState(null);
  const [settings, setSettings] = useState({});
  const [team, setTeam] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {}));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (team) params.set("team", team);
    if (ageGroup) params.set("age_group", ageGroup);
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/dashboard${qs}`).then((r) => setData(r.data));
    api.get("/awards").then((r) => setAwards(r.data));
  }, [team, ageGroup]);

  if (!data) return <div className="overline">Loading…</div>;

  const isFiltered = !!(team || ageGroup);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Overview</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Dashboard</h1>
          {isFiltered && (
            <div className="text-xs text-[#FFD166] mt-2 font-bold uppercase tracking-wider">
              Filtered: {[team, ageGroup].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            data-testid="dashboard-filter-team"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="h-11 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md text-sm"
          >
            <option value="">All Teams</option>
            {(settings.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            data-testid="dashboard-filter-age-group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            className="h-11 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md text-sm"
          >
            <option value="">All Age Groups</option>
            {(settings.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {isFiltered && (
            <button
              onClick={() => { setTeam(""); setAgeGroup(""); }}
              data-testid="dashboard-filter-clear"
              className="h-11 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md text-sm text-neutral-400 hover:border-[#D90429] hover:text-white"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Players" value={data.total_players} icon={Users} testid="stat-total-players" to="/players" sub="Tap to view roster" />
        <StatCard label="Present Today" value={data.present_today} icon={CheckCircle} accent="#06D6A0" testid="stat-present-today" to="/attendance" />
        <StatCard label="Absent Today" value={data.absent_today} icon={XCircle} accent="#D90429" testid="stat-absent-today" to="/attendance" />
        <StatCard label="Attendance" value={`${data.attendance_pct}%`} icon={Percent} accent="#FFD166" testid="stat-attendance-pct" to="/attendance" />
      </div>

      {/* Quick Actions */}
      <div>
        <div className="overline mb-3">Quick Actions</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <QuickAction to="/players/new" label="Add Player" icon={UserPlus} color="#D90429" />
          <QuickAction to="/attendance" label="Attendance" icon={ClipboardText} color="#06D6A0" />
          <QuickAction to="/matches" label="Match Stats" icon={Trophy} color="#FFD166" />
          <QuickAction to="/practices" label="Practice Rating" icon={Star} color="#06D6A0" />
          <QuickAction to="/announcements" label="Announce" icon={Megaphone} color="#D90429" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's practice */}
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-2">Today's Practice</div>
          {data.today_practice ? (
            <>
              <div className="font-display font-bold text-2xl uppercase">{data.today_practice.title}</div>
              <div className="text-sm text-neutral-400 mt-1">{data.today_practice.duration_minutes} min</div>
              {data.today_practice.activities?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {data.today_practice.activities.map((a, i) => (
                    <li key={i} className="text-sm text-neutral-300">• {a}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="text-neutral-500 text-sm">No practice scheduled today.</div>
          )}
        </div>

        {/* Upcoming match */}
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-2">Upcoming Match</div>
          {data.upcoming_match ? (
            <>
              <div className="font-display font-black text-2xl uppercase tracking-tight">
                vs {data.upcoming_match.opponent}
              </div>
              <div className="text-sm text-neutral-400 mt-1">
                {data.upcoming_match.match_date} {data.upcoming_match.match_time && `• ${data.upcoming_match.match_time}`}
              </div>
              <div className="text-sm text-neutral-500 mt-1">{data.upcoming_match.location}</div>
              <div className="text-xs text-neutral-500 mt-2 uppercase tracking-widest">{data.upcoming_match.competition}</div>
            </>
          ) : (
            <div className="text-neutral-500 text-sm">No matches scheduled.</div>
          )}
        </div>

        {/* Player of the Month */}
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-3">Player of the Month</div>
          {data.player_of_month ? (
            <Link to={`/players/${data.player_of_month.id}`} className="flex items-center gap-4">
              <PlayerAvatar player={data.player_of_month} size={64} />
              <div>
                <div className="font-display font-bold text-xl">
                  {data.player_of_month.first_name} {data.player_of_month.last_name}
                </div>
                <div className="text-sm text-[#FFD166] font-bold">★ {data.player_of_month.overall_rating || 0} / 5</div>
                <div className="overline mt-1">{data.player_of_month.position}</div>
              </div>
            </Link>
          ) : (
            <div className="text-neutral-500 text-sm">Rate players to see leader.</div>
          )}
        </div>
      </div>

      {/* Awards */}
      {awards && Object.values(awards).some(Boolean) && (
        <div>
          <div className="overline mb-3">Awards</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <AwardCard title="Player of the Month" data={awards.player_of_month} icon={Trophy} color="#FFD166" testid="award-pom" />
            <AwardCard title="Top Scorer" data={awards.top_scorer} icon={Ball} color="#D90429" suffix="goals" testid="award-scorer" />
            <AwardCard title="Best Attendance" data={awards.best_attendance} icon={Medal} color="#06D6A0" suffix="%" testid="award-attendance" />
            <AwardCard title="Most Improved" data={awards.most_improved} icon={TrendUp} color="#FFD166" suffix="↑" testid="award-improved" />
            <AwardCard title="Best Defender" data={awards.best_defender} icon={Shield} color="#F5F5F5" suffix="/5" testid="award-defender" />
            <AwardCard title="Best Team Player" data={awards.best_team_player} icon={HandHeart} color="#06D6A0" suffix="/5" testid="award-teamplayer" />
          </div>
        </div>
      )}

      {/* Birthdays & Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6" data-testid="dashboard-birthdays-week">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 overline">
              <Cake size={14} weight="bold" /> Birthdays this week
            </div>
            <Link to="/birthdays" data-testid="dashboard-birthdays-view-all" className="text-xs font-bold uppercase tracking-wider text-[#D90429] hover:text-white">
              View all
            </Link>
          </div>
          {data.birthdays?.length ? (
            <ul className="space-y-3">
              {data.birthdays.map((b) => (
                <li key={b.id} className="flex items-center gap-3" data-testid={`dashboard-birthday-${b.id}`}>
                  <PlayerAvatar player={b} size={36} />
                  <Link to={`/players/${b.id}`} className="text-sm flex-1 min-w-0">
                    <div className="font-semibold truncate">{b.name}</div>
                    <div className="text-xs text-neutral-500">
                      Turns {b.turning} · {b.age_group}
                    </div>
                  </Link>
                  <span className={`text-xs font-bold uppercase ${b.days_until === 0 ? "text-[#FFD166]" : "text-neutral-400"}`}>
                    {b.days_until === 0 ? "Today" : b.days_until === 1 ? "Tomorrow" : `in ${b.days_until}d`}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-neutral-500 text-sm">No birthdays in the next 7 days.</div>
          )}
        </div>

        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6">
          <div className="overline mb-3">Recent Activity</div>
          {(data.recent_announcements?.length || data.recent_notes?.length) ? (
            <ul className="space-y-3">
              {data.recent_announcements.map((a) => (
                <li key={a.id} className="border-l-2 border-[#D90429] pl-3">
                  <div className="text-sm font-semibold">{a.title}</div>
                  <div className="text-xs text-neutral-500">Announcement • {a.author_name}</div>
                </li>
              ))}
              {data.recent_notes.map((n) => (
                <li key={n.id} className="border-l-2 border-[#06D6A0] pl-3">
                  <div className="text-sm text-neutral-300">{n.text}</div>
                  <div className="text-xs text-neutral-500">Note • {n.author_name}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-neutral-500 text-sm">No recent activity.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const AwardCard = ({ title, data, icon: Icon, color, suffix = "", testid }) => (
  <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4 card-lift" data-testid={testid}>
    <div className="flex items-center gap-2 overline">
      <Icon size={14} weight="bold" color={color} /> {title}
    </div>
    {data?.player ? (
      <Link to={`/players/${data.player.id}`} className="mt-3 block">
        <div className="flex items-center gap-3">
          <PlayerAvatar player={data.player} size={40} />
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate">{data.player.first_name} {data.player.last_name}</div>
            <div className="text-xs font-bold" style={{ color }}>{data.value}{suffix}</div>
          </div>
        </div>
      </Link>
    ) : (
      <div className="text-xs text-neutral-500 mt-3">Not enough data yet.</div>
    )}
  </div>
);
